import { BehaviorSubject, Observable } from 'rxjs'
import { Http } from '@/services/http'
import { Injectable } from '@tanbo/vue-di-plugin'
import Cookies from 'js-cookie'
import { COOKIE_CONFIG } from '@/utils/tool'
import { NotifyPopupService, PopupKey } from '@/views/dashboard/_notify-popup/notify-popup.service'
import { useRolePermStore } from '@/services/role-perm.service'
import { messageService } from '@/common/basis/root-injector'
import { IS_PRIVATE } from '@/common/const/env'

export interface TeamInfo {
  team_id: number
  name: string
  pin_code: string
  exit_code: string
  uninstall_code: string
  is_creator: boolean
  role_name?: string
  role_key?: string
  used_days: number
  terminal_count?: number
  show_vip_modify?: boolean
}

export interface EffectivePeriod {
  /** 开始时间戳,单位秒 */
  begin_time_stamp: number
  /** 过期时间戳,单位秒 */
  expires_time_stamp: number
}

export interface TeamListResponse {
  list: TeamInfo[]
}

let currentTeam: TeamInfo
let teamList: TeamInfo[] = []

try {
  currentTeam = JSON.parse(Cookies.get('currentTeam') || '{}') as TeamInfo
  teamList = JSON.parse(localStorage.getItem('teamList') || '[]')
} catch (e) {
  currentTeam = {} as TeamInfo
  teamList = []
}

@Injectable()
export class Team {
  currentTeam: Observable<TeamInfo>
  teamList: Observable<TeamInfo[]>
  private updateTeamEvent = new BehaviorSubject<TeamInfo>(currentTeam)

  private _hasTeam = !!currentTeam?.team_id

  private updateTeamListEvent = new BehaviorSubject<TeamInfo[]>(teamList.length ? teamList : [currentTeam])

  constructor (private http: Http, private notifyPopup: NotifyPopupService) {
    this.currentTeam = this.updateTeamEvent.asObservable()
    this.teamList = this.updateTeamListEvent.asObservable()
    this.http.addCommonRequestFields({
      team_id: currentTeam?.team_id
    })
  }

  getCurrentTeam () {
    return this.updateTeamEvent.getValue()
  }

  cleanTeam () {
    this.updateCurrentTeam(null)
    localStorage.removeItem('teamList')
    this.updateTeamListEvent.next([])
    this._hasTeam = false
  }

  hasTeam () {
    return this._hasTeam
  }

  hasSelectedTeam () {
    return Cookies.get('currentTeam')?.includes('team_id')
  }

  async updateCurrentTeam (c: TeamInfo) {
    const oldTeamInfo = JSON.parse(Cookies.get('currentTeam') || '{}')
    const isChangeTeam = c?.team_id !== oldTeamInfo?.team_id

    this._hasTeam = !!c?.team_id
    this.http.addCommonRequestFields({ team_id: c?.team_id || undefined })
    if (c) {
      Cookies.set('currentTeam', JSON.stringify(c), COOKIE_CONFIG)
      currentTeam = Object.assign({}, currentTeam, c)
    } else {
      currentTeam = null
      Cookies.remove('currentTeam', COOKIE_CONFIG)
    }
    this.updateTeamEvent.next({
      ...c
    })
    if (isChangeTeam && this._hasTeam) {
      try {
        await this.switchTeam()
      } catch (e) {
        // 切换团队API异常，回滚本地状态，不允许切换到该团队
        if (oldTeamInfo?.team_id) {
          this.http.addCommonRequestFields({ team_id: oldTeamInfo.team_id })
          Cookies.set('currentTeam', JSON.stringify(oldTeamInfo), COOKIE_CONFIG)
          currentTeam = { ...oldTeamInfo }
          this._hasTeam = true
          this.updateTeamEvent.next({ ...oldTeamInfo })
        } else {
          this.http.addCommonRequestFields({ team_id: undefined })
          Cookies.remove('currentTeam', COOKIE_CONFIG)
          currentTeam = null
          this._hasTeam = false
          this.updateTeamEvent.next(null)
        }
        throw e
      }
      this.updatePopupStatus()
      useRolePermStore().initCurrentRolePerms(true)
      messageService.startPollUnread()
    }
  }

  updatePopupStatus () {
    this.notifyPopup.unReadyTeamPopup()
    this.notifyPopup.popupToReady(PopupKey.vipModify)
  }

  updateTeamList (c: TeamInfo[]) {
    localStorage.setItem('teamList', JSON.stringify(c))
    this.updateTeamListEvent.next(c)
  }

  refreshTeamList () {
    return this.http.post<TeamListResponse>('/team/get', {}).then(response => {
      this.updateTeamList(response.list)

      const teamInfo = response.list.find(team => {
        return team.team_id === currentTeam?.team_id
      })

      if (teamInfo) {
        this.updateCurrentTeam(teamInfo)
      }

      return response
    })
  }

  switchTeam () {
    return this.http.post<void>('/team/switch', {})
  }

  getInviteTeamList () {
    if (IS_PRIVATE) return Promise.resolve({ list: [] })
    return this.http.post<TeamListResponse>('/team/invite_list', {}).catch(() => ({ list: [] }))
  }
}
